import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { DealCard } from '../components/DealCard';
import { RandomProducts } from '../components/RandomProducts';
import { ArticleCards } from '../components/ArticleCards';
import { DailyChallenges } from '../components/DailyChallenges';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Flame, Search, Filter, TrendingUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'all', name: 'Wszystko' },
  { id: 'elektronika', name: 'Elektronika' },
  { id: 'moda', name: 'Moda' },
  { id: 'dom', name: 'Dom' },
  { id: 'sport', name: 'Sport' },
  { id: 'jedzenie', name: 'Jedzenie' },
  { id: 'rozrywka', name: 'Rozrywka' },
  { id: 'podroze', name: 'Podróże' },
  { id: 'inne', name: 'Inne' },
];

export default function FeedPage() {
  const { token, authHeaders } = useAuth();
  const [deals, setDeals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [userReactions, setUserReactions] = useState({});

  const fetchDeals = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    
    try {
      const params = { page: pageNum, limit: 15 };
      if (category !== 'all') params.category = category;
      if (search) params.search = search;
      
      const res = await axios.get(`${API}/deals`, { params });
      if (append) {
        setDeals(prev => [...prev, ...res.data.deals]);
      } else {
        setDeals(res.data.deals);
      }
      setTotalPages(res.data.pages);
      setPage(pageNum);
    } catch {
      toast.error('Nie udało się załadować okazji');
    }
    setLoading(false);
    setLoadingMore(false);
  }, [category, search]);

  useEffect(() => {
    fetchDeals(1, false);
  }, [fetchDeals]);

  const loadMore = () => {
    if (page < totalPages && !loadingMore) {
      fetchDeals(page + 1, true);
    }
  };

  const handleReact = async (dealId, type) => {
    if (!token) { toast.error('Zaloguj się aby reagować'); return; }
    try {
      const res = await axios.post(`${API}/deals/${dealId}/react`, { type }, { headers: authHeaders() });
      // Update local state
      setUserReactions(prev => ({
        ...prev,
        [dealId]: res.data.status === 'removed' ? null : res.data.type
      }));
      // Refresh deal
      fetchDeals(1, false);
    } catch {
      toast.error('Nie udało się zareagować');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="feed-page">
      <SEO title="Gadulec" description="Najnowsze okazje od społeczności. Najlepsze promocje, rabaty i kody zniżkowe." />

      {/* Daily Challenges */}
      <DailyChallenges />

      {/* Hero */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-5 h-5 text-primary" />
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
            GADULEC
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">Najnowsze okazje od społeczności</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Szukaj okazji..."
            className="pl-10 bg-input border-transparent focus:border-primary rounded-none"
          />
        </div>
        <Button type="submit" data-testid="search-btn" className="bg-primary text-primary-foreground rounded-none px-4">
          <Search className="w-4 h-4" />
        </Button>
      </form>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-hide" data-testid="categories-bar">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            data-testid={`cat-${cat.id}`}
            onClick={() => setCategory(cat.id)}
            className={`flex-shrink-0 px-4 py-1.5 text-xs font-heading uppercase tracking-wider border transition-colors ${
              category === cat.id
                ? 'border-primary text-primary bg-primary/10'
                : 'border-border/40 text-muted-foreground hover:border-primary/30'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Deals Feed */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : deals.length === 0 ? (
        <div className="text-center py-12">
          <Flame className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">Brak okazji. Bądź pierwszy!</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="deals-list">
          {deals.map((deal, idx) => (
            <React.Fragment key={deal.id}>
              <DealCard
                deal={deal}
                onReact={handleReact}
                userReaction={userReactions[deal.id]}
              />
              {(idx + 1) % 5 === 0 && idx < deals.length - 1 && (
                <RandomProducts count={3} className="my-2" />
              )}
            </React.Fragment>
          ))}

          {page < totalPages && (
            <div className="flex justify-center pt-4">
              <Button
                data-testid="load-more-btn"
                onClick={loadMore}
                disabled={loadingMore}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-none px-8"
              >
                {loadingMore ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Ładowanie...</>
                ) : (
                  'Załaduj więcej'
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Latest blog articles */}
      <ArticleCards title="Najnowsze na blogu" limit={4} />
    </div>
  );
}
